import React from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardCheck, 
  FileText, 
  Truck, 
  Eye, 
  TrendingUp 
} from 'lucide-react';

const ProcessSection = () => {
  const steps = [
    {
      icon: ClipboardCheck,
      number: '01',
      title: 'Scope',
      description: 'We assess your requirements and operational context.',
    },
    {
      icon: FileText,
      number: '02',
      title: 'Propose',
      description: 'Receive a detailed proposal with clear deliverables.',
    },
    {
      icon: Truck,
      number: '03',
      title: 'Mobilise',
      description: 'Structured deployment with trained, accountable teams.',
    },
    {
      icon: Eye,
      number: '04',
      title: 'Monitor',
      description: 'Ongoing quality assurance and performance tracking.',
    },
    {
      icon: TrendingUp,
      number: '05',
      title: 'Optimise',
      description: 'Continuous improvement through regular reviews.',
    },
  ];

  return (
    <section
      id="process"
      className="section-padding bg-slate-50"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2
            className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 uppercase tracking-tight mb-4"
            data-testid="process-headline"
          >
            Our <span className="text-accent">Approach</span>
          </h2>
          <p className="font-manrope text-lg text-slate-600 max-w-2xl mx-auto">
            A structured framework from initial engagement to ongoing partnership.
          </p>
        </motion.div>

        {/* Process Steps - Desktop */}
        <div className="hidden md:block relative">
          {/* Connector Line */}
          <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-slate-200 via-primary to-slate-200" />
          
          <div className="grid grid-cols-5 gap-4 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="flex flex-col items-center"
                data-testid={`process-step-${index}`}
              >
                {/* Icon Circle */}
                <div className="w-16 h-16 bg-white border-2 border-primary rounded-full flex items-center justify-center mb-6 shadow-lg relative">
                  <step.icon className="w-7 h-7 text-primary" />
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent rounded-full flex items-center justify-center">
                    <span className="font-oswald text-xs font-bold text-white">{step.number}</span>
                  </div>
                </div>
                
                {/* Content */}
                <h3 className="font-oswald text-xl font-bold text-slate-800 uppercase tracking-tight mb-2 text-center">
                  {step.title}
                </h3>
                <p className="font-manrope text-sm text-slate-600 text-center">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Steps - Mobile */}
        <div className="md:hidden space-y-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="flex items-start gap-4 p-6 bg-white rounded-sm border border-slate-200"
              data-testid={`process-step-mobile-${index}`}
            >
              <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center flex-shrink-0">
                <step.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-oswald text-sm font-bold text-accent">{step.number}</span>
                  <h3 className="font-oswald text-lg font-bold text-slate-800 uppercase tracking-tight">
                    {step.title}
                  </h3>
                </div>
                <p className="font-manrope text-sm text-slate-600">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
